import { Button, Icon, Tooltip } from "@wordpress/components";
import { __ } from "@wordpress/i18n";
import {
	check,
	download,
	external,
	close
} from "@wordpress/icons";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { useImageContext } from "../context/ImageContext";
import DownloadProgress from "./Loader";
import { PROVIDERS, TransparentBG ,ucfirst} from "../helpers"
const ajaxUrl = image_hub_data.ajaxurl;
const nonce = image_hub_data.nonce;

Masonry.getEqualCountColumns = function (children, columnsCount) {
	const columns = Array.from({ length: columnsCount }, () => []);
	const columnHeights = Array(columnsCount).fill(0);
	const childRefs = [];
	const containerWidth = window.innerWidth;
	const gutter = 0; // You can adjust this if needed
	const totalGutter = gutter * (columnsCount - 1);
	const columnWidth = (containerWidth - totalGutter) / columnsCount;

	let validIndex = 0;

	React.Children.forEach(children, (child, idx) => {
		if (child && React.isValidElement(child)) {
			const ref = React.createRef();
			childRefs.push(ref);

			const aspectRatio = child.props.aspectRatio || 1;
			const estimatedHeight = Math.floor(columnWidth / aspectRatio);
			const targetColumnIndex = columnHeights.indexOf(
				Math.min(...columnHeights),
			);
			columnHeights[targetColumnIndex] += estimatedHeight;

			columns[targetColumnIndex].push(
				<div
					key={child.key || idx}
					style={{
						display: "flex",
						justifyContent: "stretch",
						maxHeight: `${estimatedHeight}px`,
						minHeight: `${estimatedHeight}px`,
						overflow: "hidden",
					}}
					// key={validIndex}
					ref={ref}
				>
					{child}
				</div>,
			);

			validIndex++;
		}
	});

	return { columns, childRefs };
};

const ImageGrid = ({ setModalImageUrl, media }) => {
	// Added 'media' prop
	const {
		errors,
		images,
		fetchImages,
		pagination,
		searchTerm,
		provider,
		isLoading,
		removeImage,
	} = useImageContext();
	const { currentPage, hasNextPage } = pagination;
	const observer = useRef();
	const [downloadedImages, setDownloadedImages] = useState({});
	const [downloadingImages, setDownloadingImages] = useState({});
	const [erroredImages, setErroredImages] = useState({});
	const [inLibrary, setImagesInLibrary] = useState([]);
	useEffect(() => {
		setDownloadedImages([]);
		setDownloadingImages([]);
	}, [provider]);

	useEffect(() => {
		fetch(
			image_hub_data.adminUrl.replace("/wp-admin", "") +
				"wp-json/wp/v2/media?&per_page=100&_fields=source_url",
		)
			.then((response) => response.json())
			.then((imagezs) => {
				const uniqueFilenames = new Set();

				imagezs.forEach((url) => {
					uniqueFilenames.add(
						url.source_url.split("/").pop().split(".")[0],
					);
				});
				if (!![...uniqueFilenames]?.length)
					setImagesInLibrary([...uniqueFilenames]);
			})
			.catch((error) => console.error("Error fetching images:", error));
	}, [downloadedImages]);

	const formattedImages =
		images?.map((img, idx) => ({
			preview: img.thumbnailUrl,
			fullImage: img.url,
			still: img?.still || undefined,
			id: img.provider + "-" + img.id,
			external: img.external,
			provider: img.provider,
			user: img.user,
			about: img.about,
			height: img.height,
			aspectRatio: img.width / img.height,
			"user-profile": img.user?.link,
			downloadCallback: img.downloadCallback || undefined,
		})) || [];

	// const handleZoom = (photo) => {
	// 	setModalImageUrl?.(photo.fullImage);
	// };

	const handleOpenExternal = (photo, path = "external") => {
		if (photo?.[path]) {
			window.open(photo?.[path], "_blank");
		}
	};

	const openMediaLibrary = (attachmentId) => {
		// Ensure wp.media exists
		const mediaModalEl = wp.media.frame.el;
		const mediaTab = mediaModalEl.querySelector("#menu-item-browse");
		if (mediaTab) {
			// Open the 'Media Library' tab.
			mediaTab.click();
		}

		// Delay to allow for tab switching
		setTimeout(function () {
			if (wp.media.frame.content.get() !== null) {
				// Force a refresh of the mdeia modal content.
				wp.media.frame.content.get().collection._requery(true);
			}

			// Select the attached that was just uploaded.
			const selection = wp.media.frame.state().get("selection");
			const selected = parseInt(attachmentId);
			selection.reset(selected ? [wp.media.attachment(selected)] : []);
		}, 100);
	};

	const handleDownload = (photo) => {
		if (downloadedImages[photo.id]) return;

		setDownloadingImages((prev) => ({ ...prev, [photo.id]: 0 }));

		let progress = 0;
		const progressInterval = setInterval(() => {
			const increment = progress < 30 ? 0.5 : progress < 70 ? 1 : 0.5;
			progress = Math.min(progress + increment, 90); // Cap at 90% until fetch completes

			setDownloadingImages((prev) => ({ ...prev, [photo.id]: progress }));
		}, 50);

		fetch(ajaxUrl, {
			method: "POST",
			body: new URLSearchParams({
				action: "image_hub_download_image_to_library",
				image_url: photo.fullImage,
				nonce: nonce,
				name: photo.id,
				provider: photo.provider,
				link: photo.user.link,
				username: photo.user.name,
				title: photo.about.title,
				description: photo.about.description,
				alt: photo.about.alt,
				downloadImageCallbackUrl: photo.downloadCallback || "",
			}),
		})
			.then((response) => response.json())
			.then((data) => {
				clearInterval(progressInterval);

				if (data.success) {
					setDownloadingImages((prev) => ({
						...prev,
						[photo.id]: 100,
					}));

					setTimeout(() => {
						setDownloadingImages((prev) => ({
							...prev,
							[photo.id]: false,
						}));
						setDownloadedImages((prev) => ({
							...prev,
							[photo.id]: data.data.attachment?.id,
						}));

						if (media) openMediaLibrary(data.data.attachment?.id);
					}, 300);
				} else {
					setErroredImages((prev) => ({ ...prev, [photo.id]: true }));
					setDownloadingImages((prev) => ({
						...prev,
						[photo.id]: false,
					}));
				}
			})
			.catch((error) => {
				clearInterval(progressInterval);
				setErroredImages((prev) => ({ ...prev, [photo.id]: true }));
				setDownloadingImages((prev) => ({
					...prev,
					[photo.id]: false,
				}));
				console.error("Error downloading image:", error);
			});
	};

	const lastImageRef = useCallback(
		(node) => {
			if (isLoading) return;
			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasNextPage) {
					fetchImages(searchTerm, provider, currentPage + 1);
				}
			});
			if (node) observer.current.observe(node);
		},
		[
			isLoading,
			hasNextPage,
			searchTerm,
			provider,
			currentPage,
			fetchImages,
		],
	);
	return (
		<>
			{formattedImages.length === 0 && !isLoading ? (
				<>
					{errors &&
					errors != {} &&
					!Object.values(errors).some((x) =>
						x?.toLowerCase().includes("uri too long"),
					) ? (
						<ErrorDisplay errors={errors} />
					) : (
						<NoResultDisplay />
					)}
				</>
			) : (
				<div className="image-hub-galery-container">
					{/* Why did i put 100vh? IF there is some error come back add this into style style={{ minHeight: "100vh" }} */}
					<div>
						<ResponsiveMasonry
							columnsCountBreakPoints={{
								350: 1,
								480: 1,
								768: 2,
								1024: 3,
								1200: 4,
								1600: 6,
							}}
							gutterBreakpoints={{
								350: "8px",
								480: "10px",
								768: "12px",
								1024: "16px",
								1200: "20px",
								1600: "24px",
							}}
						>
							<Masonry
								columnsCount={5}
								gutter="5px"
								formattedImages={formattedImages}
							>
								{formattedImages.map((photo, idx) => (
									<PhotoItem
										key={photo.fullImage + photo.id}
										photo={photo}
										aspectRatio={photo.aspectRatio}
										removeImage={removeImage}
										downloadingImages={downloadingImages}
										downloadedImages={downloadedImages}
										handleOpenExternal={handleOpenExternal}
										inLibrary={inLibrary}
										media={media}
										handleDownload={handleDownload}
										erroredImages={erroredImages}
									/>
								))}
							</Masonry>
						</ResponsiveMasonry>
					</div>
					{!isLoading && !hasNextPage && (
						<div className="image-hub-gradient-banner">
							<h2>
								{__(
									"These are all the images for the current search terms and filters",
									"image-hub",
								)}
							</h2>
						</div>
					)}
					{!isLoading && hasNextPage && (
						<div
							ref={lastImageRef}
							style={{
								height: "20px",
							}}
						></div>
					)}
				</div>
			)}
		</>
	);
};

import { cautionFilled } from "@wordpress/icons";
import { getImageHubSettings } from "../context/useSettings";
import Marquee from "react-fast-marquee";

const hasCertainKey = (provider, data) =>
	Object.entries(data).some(
		([key, value]) =>
			key.startsWith("image_hub_api_keys_" + provider) && value,
	);

const ErrorDisplay = ({ errors }) => {
	const { imageHubSettings } = getImageHubSettings();
	const { provider } = useImageContext();
	const hasPersonalApiKey = hasCertainKey(
		provider == "all" ? "giphy" : provider,
		imageHubSettings,
	);

	let errorMessage = "";
	let errorProvider = provider;

	if (provider === "All" && errors && typeof errors === "object") {
		const firstErrorEntry = Object.entries(errors).find(
			([key, value]) => value,
		);
		if (firstErrorEntry) {
			errorProvider = firstErrorEntry[0];
			errorMessage = firstErrorEntry[1];
		}
	} else if (errors && errors[provider]) {
		errorMessage = errors[provider];
	}

	return (
		<div className="image-hub-error-card">
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Icon fill="#cc1818" size={36} icon={cautionFilled} />
				<div
					style={{
						fontSize: "16px",
						color: "#cc1818",
						fontWeight: 700,
					}}
				>
					{__("Image Library Error", "image-hub")}
				</div>
				<div
					style={{
						fontSize: "13px",
						marginTop: "10px",
						textWrap: "wrap",
						textAlign: "center",
						justifyContent: "center",
					}}
				>
					{__(
						"We couldn’t fetch images from the library right now. This may be due to a temporary issue with the external image source. Please try again later.",
						"image-hub",
					)}
				</div>
				{errorMessage && (
					<div style={{ fontSize: "16px", marginTop: "10px" }}>
						{errorMessage.replace(
							"401:Unauthorized",
							"Invalid ApiKey",
						)}
					</div>
				)}
			</div>
		</div>
	);
};
const NoResultDisplay = () => {
	return (
		<div className="image-hub-error-card">
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Icon fill="#FFB200" size={36} icon={cautionFilled} />
				<div
					style={{
						fontSize: "16px",
						color: "#FFB200",
						fontWeight: 700,
					}}
				>
					{__("No results found", "image-hub")}
				</div>
				<div
					style={{
						fontSize: "13px",
						marginTop: "10px",
						textWrap: "wrap",
						textAlign: "center",
						justifyContent: "center",
					}}
				>
					{__(
						"We could not find any images for your search term. This may be due to a temporary issue or the search term might be too specific",
						"image-hub",
					)}
				</div>
			</div>
		</div>
	);
};
export default ImageGrid;

const PhotoItem = React.forwardRef(
	(
		{
			photo,
			removeImage,
			downloadingImages,
			downloadedImages,
			handleOpenExternal,
			inLibrary,
			media,
			handleDownload,
			erroredImages,
		},
		ref,
	) => {
		let hoverTimeout;
		const [hovered, setHovered] = useState(false);

		return (
			<div
				ref={ref}
				className={
					"image-hub-image-container image-hub-image-container-hidden " +
					photo.id
				}
				key={photo.fullImage + photo.id}
				onMouseEnter={(e) => {
					setHovered(true);
					e.preventDefault();
					e.stopPropagation();
					if (photo.provider !== PROVIDERS.GIPHY.name) return;

					const className = `.image-hub-image${downloadingImages[photo.id] || downloadedImages[photo.id] ? ".image-hub-downloading" : ""}`;
					const comp =
						e?.target?.parentElement?.querySelector(className);
					if (comp) {
						comp.src = photo.preview;

						clearTimeout(hoverTimeout);

						hoverTimeout = setTimeout(() => {
							comp.src = photo.still;
						}, 1500);
					}
				}}
				onMouseLeave={(e) => {
					setHovered(false);
					e.preventDefault();
					e.stopPropagation();
					if (photo.provider !== PROVIDERS.GIPHY.name) return;

					const className = `.image-hub-image${downloadingImages[photo.id] || downloadedImages[photo.id] ? ".image-hub-downloading" : ""}`;
					const comp =
						e?.target?.parentElement?.querySelector(className);
					if (comp) {
						clearTimeout(hoverTimeout);
						comp.src = photo.still;
					}
				}}
			>
				<img
					//if needed
					// style={{ background: `url(${TransparentBG})`,backgroundSize:"cover" }}
					style={{ aspectRatio: photo.aspectRatio }}
					src={photo.still || photo.preview}
					alt=""
					onLoad={(e) => {
						e.target.parentElement.classList.remove(
							"image-hub-image-container-hidden",
						);
						// e.target.style.padding=0
					}}
					onError={(e) => {
						if (e.target.src == photo.fullImage) {
							e.target.remove();
							removeImage(photo.id);
						} else {
							e.target.src = photo.fullImage;
						}
					}}
					className={`image-hub-image ${downloadingImages[photo.id] || downloadedImages[photo.id] ? "image-hub-downloading" : ""}`}
				/>
				{!downloadedImages[photo.id] &&
					!downloadingImages[photo.id] && (
						<div className="image-hub-image-overlay">
							<Tooltip
								delay={0}
								text={__(
									`View on ${ucfirst(photo.provider)}`,
									"image-hub",
								)}
							>
								<div
									className="image-hub-zoom-btn"
									onClick={() => handleOpenExternal(photo)}
								>
									<Icon fill="white" icon={external} />
								</div>
							</Tooltip>
							<Tooltip
								key={1}
								delay={0}
								text={
									__("Get to know ", "image-hub") +
									" " +
									photo.user.name
								}
							>
								<div
									className="image-hub-credits-avatar"
									onClick={() =>
										handleOpenExternal(
											photo,
											"user-profile",
										)
									}
								>
									{photo.user.profile_image ? (
										<img
											className="image-hub-credits-avatar-image"
											src={photo.user.profile_image}
										/>
									) : (
										<img
											className="image-hub-credits-avatar-image"
											style={{ objectFit: "contain" }}
											src={
												PROVIDERS[
													photo.provider.toUpperCase()
												].icon
											}
										/>
									)}
									{hovered && (
										<Marquee
											delay={0.5}
											speed={10}
											play={
												(photo.user.name
													? `${photo.user.name} (${photo.provider})`
													: photo.provider
												).length > 25
											}
										>
											{photo.user.name
												? `${photo.user.name} (${ucfirst(photo.provider)})`
												: ucfirst(photo.provider)}{" "}
										</Marquee>
									)}
								</div>
							</Tooltip>
							<Tooltip
								key={2}
								delay={0}
								text={
									inLibrary?.includes(photo.id?.toString())
										? __("Already downloaded", "image-hub")
										: __("Download", "image-hub")
								}
							>
								<div
									className="image-hub-download-btn"
									onClick={() => {
										if (
											inLibrary?.includes(
												photo.id?.toString(),
											)
										)
											return;
										if (media) {
											handleDownload(photo);
										} else {
											handleDownload(photo);
										}
									}}
								>
									<div>
										{inLibrary?.includes(
											photo.id?.toString(),
										) ? (
											<div
												key="ttt1"
												className="image-hub-circle-icon"
												style={{
													width: "16px",
													height: "16px",
												}}
											>
												<Icon
													size={16}
													fill="white"
													icon={check}
												/>
											</div>
										) : (
											<Icon
												key="ttt1"
												fill="white"
												icon={download}
											/>
										)}
									</div>
								</div>
							</Tooltip>
						</div>
					)}
				{downloadedImages[photo.id] && (
					<div className="image-hub-image-download-overlay">
						<div className="image-hub-circle-icon">
							<Icon size={24} fill="white" icon={check} />
						</div>
						<div
							style={{
								color: "white",
								marginTop: "10px",
								lineHeight: "100%",
							}}
						>
							{__("Downloaded to Media Gallery", "image-hub")}
						</div>
						<Button
							style={{ marginTop: "20px" }}
							variant="primary"
							onClick={() => {
								window.location.href =
									image_hub_data.adminUrl +
									"upload.php?item=" +
									downloadedImages[photo.id];
							}}
						>
							{__("View Image", "image-hub")}
						</Button>
					</div>
				)}
				<div style={{ maxHeight: "100%", overflow: "hidden" }}>
					{downloadingImages[photo.id] && (
						<div className="image-hub-image-download-overlay">
							<DownloadProgress
								progressValue={downloadingImages[photo.id]}
							/>
						</div>
					)}
				</div>
				{erroredImages[photo.id] && (
					<div className="image-hub-image-download-overlay">
						<div className="image-hub-image-download-overlay">
							<div
								className="image-hub-circle-icon"
								style={{
									background: "red",
									width: "30px",
									height: "32px",
								}}
							>
								<Icon size={24} fill="white" icon={close} />
							</div>
							<div
								style={{
									color: "white",
									marginTop: "10px",
									lineHeight: "100%",
								}}
							>
								{__(
									"Image could not be downloaded!",
									"image-hub",
								)}
							</div>
						</div>
					</div>
				)}
			</div>
		);
	},
);

